import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Building2, Globe, DollarSign, Tag, TrendingUp, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getStats().then(setStats).catch(console.error).finally(() => setLoading(false));
  }, []);

  const cards = [
    { label: 'Anbieter', value: stats?.providers || 0, sub: `${stats?.active_providers || 0} aktiv`, icon: Building2, color: 'sky', link: '/admin/providers' },
    { label: 'TLDs', value: stats?.tlds || 0, sub: 'Endungen', icon: Globe, color: 'emerald', link: '/admin/tlds' },
    { label: 'Preise', value: stats?.prices || 0, sub: 'Einträge', icon: DollarSign, color: 'amber', link: '/admin/prices' },
    { label: 'Aktionen', value: stats?.active_promotions || 0, sub: 'Aktive', icon: Tag, color: 'purple', link: '/admin/promotions' },
  ];

  const colorMap = {
    sky: 'bg-sky-50 text-sky-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Übersicht über alle Daten</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(card => (
          <Link key={card.label} to={card.link}>
            <Card className="rounded-2xl hover:shadow-md transition-shadow duration-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[card.color]}`}>
                    <card.icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-bold tabular-nums text-slate-900">{card.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{card.label} · {card.sub}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Schnellaktionen</h3>
            <div className="space-y-2">
              <Link to="/admin/providers" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <Building2 className="w-4 h-4 text-sky-600" />
                <span className="text-sm">Neuen Anbieter anlegen</span>
              </Link>
              <Link to="/admin/tlds" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <Globe className="w-4 h-4 text-emerald-600" />
                <span className="text-sm">Neue TLD anlegen</span>
              </Link>
              <Link to="/admin/import" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <Upload className="w-4 h-4 text-amber-600" />
                <span className="text-sm">Preisliste importieren</span>
              </Link>
              <Link to="/admin/promotions" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <Tag className="w-4 h-4 text-purple-600" />
                <span className="text-sm">Neue Aktion erstellen</span>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">System-Info</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Datenbank</span>
                <span className="text-emerald-600 font-medium">Verbunden</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Aktive Anbieter</span>
                <span className="font-medium">{stats?.active_providers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Aktive Aktionen</span>
                <span className="font-medium">{stats?.active_promotions || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
